import * as React from 'react'

import { Button, Radio } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import createMemo from 'modules/core/hooks/createMemo'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import membershipsCollection from 'modules/teams/store/collections/memberships'
import { membershipIsLead } from 'modules/teams/store/membershipIsLead'
import type { ITeam } from 'modules/teams/store/models/Team'
import { teamMemberships } from 'modules/teams/store/teamMemberships'

interface Props {
  team: ITeam
  onClose: () => void
  selectedGroup?: string
}

const filterEmployees = createMemo((e: Employee[], membershipEmployeeIds: number[]) => {
  return e.filter((employee) => !membershipEmployeeIds.includes(employee.attributes.id))
})

const MembersModal = ({ team, onClose, selectedGroup = '' }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<number[]>([])
  const memberships = teamMemberships(membershipsCollection, employees, team.id)
  const membershipEmployeeIds = memberships.map((m) => m.attributes.employee_id)
  const [group, setGroup] = React.useState(selectedGroup)
  const sortEmployees = (input: Employee[]) =>
    orderBy(input, (e) => employeeUser(e).fullName(), ['asc'])
  const employeesToShow = filterEmployees(employees.active, membershipEmployeeIds)
  const leaders = memberships.filter((membership) => membershipIsLead(employees, membership))

  const leadersToShow = employees.active.filter(
    (x) => !leaders.some((y) => y.attributes.employee_id === x.attributes.id)
  )
  const onSelectEmployee = (id: number | null | undefined) => {
    setSelectedEmployeeIds(id ? xor(selectedEmployeeIds, [id]) : [])
  }

  const onSelectMultiple = (selectedIds: number[]) => {
    setSelectedEmployeeIds(selectedIds)
  }

  const onSubmit = async () => {
    const { success, value } = await asyncAction(() =>
      membershipsCollection.rpc('bulk_upsert', {
        team_id: team.id,
        employees_ids: group === 'members' ? selectedEmployeeIds : [],
        lead_employees_ids: group === 'leaders' ? selectedEmployeeIds : [],
      })
    )

    track(trackingEvents.teams.addTeamDetailsMembersSubmit, {
      is_successful: success,
      error_code: !success && JSON.stringify(value),
      number_of_members: selectedEmployeeIds.length,
    })

    if (success) {
      onClose()
      membershipsCollection.set(value, { add: true, change: true, remove: false })
    }

    setSelectedEmployeeIds([])
  }

  const handleClose = () => {
    onClose()
    track(trackingEvents.teams.addTeamDetailsMembersCancel)
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        title={i18n.t('teams.members_modal.title')}
        description={i18n.t('teams.members_modal.description')}
        illustration="group"
        onClose={handleClose}
        onSubmit={onSubmit}
        footer={<Button submit>{i18n.t('common.submit')}</Button>}
      >
        <Radio.Wrap>
          <Radio
            name={i18n.t('teams.add_team_members')}
            value={group}
            onChange={(value) => setGroup(value)}
            description={i18n.t('teams.members.new_description')}
            option={{
              label: i18n.t('teams.add_team_members'),
              value: 'members',
            }}
          >
            <EmployeeSelector
              employees={sortEmployees(employeesToShow)}
              onSelectEmployee={onSelectEmployee}
              selectedEmployeeIds={selectedEmployeeIds}
              onSelectMultiple={onSelectMultiple}
            />
          </Radio>
        </Radio.Wrap>
        <Radio.Wrap>
          <Radio
            name={i18n.t('teams.add_team_leaders')}
            value={group}
            onChange={(value) => setGroup(value)}
            description={i18n.t('teams.leaders_modal.description')}
            option={{
              label: i18n.t('teams.add_team_leaders'),
              value: 'leaders',
            }}
          >
            <EmployeeSelector
              employees={sortEmployees(leadersToShow)}
              onSelectEmployee={onSelectEmployee}
              selectedEmployeeIds={selectedEmployeeIds}
              onSelectMultiple={onSelectMultiple}
            />
          </Radio>
        </Radio.Wrap>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(MembersModal)
